import { CopyOutlined, CreditCardOutlined, HistoryOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Card, Col, DatePicker, Form, InputNumber, message, Modal, Row, Select, Space, Statistic, Table } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Organization, OrganizationMember, OrganizationService, QuotaUsageLogItem } from '../services/organization';

interface OrganizationOverviewProps {
  user: {
    id: number;
    username: string;
    role: 'worker' | 'admin' | 'controller';
    organization?: Organization;
  };
}

const OrganizationOverview: React.FC<OrganizationOverviewProps> = ({ user }) => {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [organizationWithInvites, setOrganizationWithInvites] = useState<any>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [quotaLogs, setQuotaLogs] = useState<QuotaUsageLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [addQuotaModalVisible, setAddQuotaModalVisible] = useState(false);
  const [addQuotaForm] = Form.useForm();
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPagination, setLogsPagination] = useState({
    current: 1,
    pageSize: 200,
    total: 0,
    totalPages: 0
  });
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(() => [
    dayjs().subtract(1, 'day'),
    dayjs()
  ]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);

  useEffect(() => {
    loadOrganizationData();
  }, []);

  const loadOrganizationData = async () => {
    try {
      setLoading(true);
      const [orgResponse, membersResponse] = await Promise.all([
        OrganizationService.getOrganizationInfo(),
        OrganizationService.getOrganizationMembers()
      ]);
      
      setOrganization(orgResponse.organization);
      setOrganizationWithInvites(membersResponse.organization);
      setMembers(membersResponse.members);
      await fetchQuotaLogs(1, logsPagination.pageSize, dateRange, []);
    } catch (error: any) {
      message.error('加载组织信息失败: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAddQuota = async (values: { quotaAmount: number }) => {
    try {
      // 暂时禁用admin用户的额度添加功能
      message.error('额度管理功能暂时禁用，请联系系统管理员');
      setAddQuotaModalVisible(false);
      addQuotaForm.resetFields();
    } catch (error: any) {
      message.error('添加额度失败: ' + (error.response?.data?.error || error.message));
    }
  };

  const fetchQuotaLogs = async (
    page: number,
    pageSize: number,
    range: [Dayjs, Dayjs],
    userIds: number[]
  ) => {
    try {
      setLogsLoading(true);
      const response = await OrganizationService.getQuotaUsageLogs({
        start: range[0].toISOString(),
        end: range[1].toISOString(),
        userIds: userIds.length > 0 ? userIds : undefined,
        page,
        limit: pageSize
      });
      setQuotaLogs(response.logs);
      setLogsPagination(response.pagination);
    } catch (error: any) {
      message.error('加载消耗记录失败: ' + (error.response?.data?.error || error.message));
    } finally {
      setLogsLoading(false);
    }
  };

  const memberColumns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => role === 'admin' ? '管理员' : '工作者',
    },

    {
      title: '加入时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('zh-CN'),
    },
  ];

  const logColumns = [
    {
      title: '操作时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString('zh-CN'),
    },
    {
      title: '操作用户',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '消耗额度',
      dataIndex: 'quotaUsed',
      key: 'quotaUsed',
      render: (used: number) => (
        <span style={{ color: '#ff4d4f' }}>
          -{used}
        </span>
      )
    },
    {
      title: '关联订单/记录',
      key: 'record',
      render: (_: any, log: QuotaUsageLogItem) => {
        const primary = log.orderNumber
          ? `${log.orderNumber}${log.title ? ` - ${log.title}` : ''}`
          : (log.title || '—');
        const metaParts: string[] = [];
        metaParts.push(`记录ID: ${log.dataRecordId ?? '—'}`);
        if (log.model) {
          metaParts.push(`模型: ${log.model}`);
        }
        if (typeof log.cardCount === 'number') {
          metaParts.push(`抽牌数: ${log.cardCount}`);
        }
        return (
          <div>
            <div style={{ color: '#262626' }}>{primary}</div>
            <div style={{ color: '#8c8c8c', fontSize: 12 }}>{metaParts.join(' · ')}</div>
          </div>
        );
      }
    }
  ];

  if (loading) {
    return <div>加载中...</div>;
  }

  if (!organization) {
    return <div>未找到组织信息</div>;
  }

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title={`组织概览 - ${organization.name}`}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="组织额度"
                  value={organization.currentQuota === -1 ? '无限制' : organization.currentQuota}
                  prefix={<CreditCardOutlined />}
                  valueStyle={{ color: organization.currentQuota === -1 ? '#1890ff' : organization.currentQuota > 50 ? '#52c41a' : organization.currentQuota > 10 ? '#faad14' : '#ff4d4f' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="管理员"
                  value={organizationWithInvites ? organizationWithInvites.adminCount : organization.adminCount}
                  suffix={`/ ${organizationWithInvites ? organizationWithInvites.maxAdminCount : organization.maxAdminCount}`}
                  prefix={<UserOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="工作者"
                  value={organizationWithInvites ? organizationWithInvites.workerCount : organization.workerCount}
                  suffix={`/ ${organizationWithInvites ? organizationWithInvites.maxWorkerCount : organization.maxWorkerCount}`}
                  prefix={<TeamOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="组织状态"
                  value={organization.status === 'active' ? '正常' : organization.status === 'suspended' ? '暂停' : '过期'}
                  valueStyle={{ color: organization.status === 'active' ? '#52c41a' : '#ff4d4f' }}
                />
              </Col>
            </Row>
            {organization.currentQuota !== -1 && (
              <div style={{ marginTop: 16 }}>
                <Button 
                  type="primary" 
                  disabled
                  title="额度管理功能暂时禁用"
                >
                  添加额度 (暂时禁用)
                </Button>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* 邀请码显示区域 */}
      {organizationWithInvites && (
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Card title="邀请码管理">
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Card size="small" title="管理员邀请码" extra={
                    <Button 
                      size="small" 
                      icon={<CopyOutlined />} 
                      onClick={() => {
                        navigator.clipboard.writeText(organizationWithInvites.adminInviteCode);
                        message.success('已复制到剪贴板');
                      }}
                    >
                      复制
                    </Button>
                  }>
                    <div style={{ fontFamily: 'monospace', fontSize: '14px', wordBreak: 'break-all' }}>
                      {organizationWithInvites.adminInviteCode}
                    </div>
                    <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                      使用人数: {organizationWithInvites.adminCount} / {organizationWithInvites.maxAdminCount}
                    </div>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card size="small" title="工作者邀请码" extra={
                    <Button 
                      size="small" 
                      icon={<CopyOutlined />} 
                      onClick={() => {
                        navigator.clipboard.writeText(organizationWithInvites.workerInviteCode);
                        message.success('已复制到剪贴板');
                      }}
                    >
                      复制
                    </Button>
                  }>
                    <div style={{ fontFamily: 'monospace', fontSize: '14px', wordBreak: 'break-all' }}>
                      {organizationWithInvites.workerInviteCode}
                    </div>
                    <div style={{ marginTop: 8, color: '#666', fontSize: '12px' }}>
                      使用人数: {organizationWithInvites.workerCount} / {organizationWithInvites.maxWorkerCount}
                    </div>
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="组织成员" extra={<span>共 {members.length} 人</span>}>
            <Table
              columns={memberColumns}
              dataSource={members}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 个成员`,
              }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card
            title={
              <Space>
                <HistoryOutlined />
                组织记录
              </Space>
            }
          >
            <Space wrap style={{ marginBottom: 12, width: '100%' }}>
              <DatePicker.RangePicker
                showTime
                allowClear={false}
                value={dateRange}
                onChange={(values) => {
                  if (values && values[0] && values[1]) {
                    setDateRange([values[0], values[1]]);
                  }
                }}
              />
              <Select
                mode="multiple"
                allowClear
                placeholder="筛选员工（多选，默认全员）"
                style={{ minWidth: 260 }}
                value={selectedUserIds}
                onChange={(value) => setSelectedUserIds(value)}
                options={members.map(m => ({
                  label: `${m.username}${m.role === 'admin' ? '（管理员）' : '（工作者）'}`,
                  value: m.id
                }))}
                maxTagCount="responsive"
              />
              <Button
                type="primary"
                onClick={() => fetchQuotaLogs(1, logsPagination.pageSize, dateRange, selectedUserIds)}
                loading={logsLoading}
              >
                查询
              </Button>
            </Space>
            <Table
              columns={logColumns}
              dataSource={quotaLogs}
              loading={logsLoading}
              rowKey={(log) => String(log.id)}
              pagination={{
                current: logsPagination.current,
                pageSize: logsPagination.pageSize,
                total: logsPagination.total,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条记录`,
                onChange: (page, pageSize) => fetchQuotaLogs(page, pageSize, dateRange, selectedUserIds)
              }}
              scroll={{ y: 400 }}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title="添加组织额度"
        open={addQuotaModalVisible}
        onCancel={() => {
          setAddQuotaModalVisible(false);
          addQuotaForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={addQuotaForm}
          layout="vertical"
          onFinish={handleAddQuota}
        >
          <Form.Item
            name="quotaAmount"
            label="添加额度数量"
            rules={[
              { required: true, message: '请输入要添加的额度数量' },
              { type: 'number', min: 1, message: '额度数量必须大于0' }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="请输入要添加的额度数量"
              min={1}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                确认添加
              </Button>
              <Button onClick={() => setAddQuotaModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OrganizationOverview;
